import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add as AddIcon, Delete as DeleteIcon, Download as DownloadIcon, Upload as UploadIcon } from '@mui/icons-material';
import { installationDeviceService } from '../services/api';
import * as XLSX from 'xlsx';

export default function InstallationDevices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [formData, setFormData] = useState({
    city: '',
    area: '',
    barcode: '',
    status: 'inwarehouse',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      setLoading(true);
      const response = await installationDeviceService.getDevices();
      setDevices(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (device = null) => {
    if (device) {
      setEditMode(true);
      setSelectedDevice(device);
      setFormData({
        city: device.city || '',
        area: device.area || '',
        barcode: device.barcode || '',
        status: device.status || 'inwarehouse',
      });
    } else {
      setEditMode(false);
      setSelectedDevice(null);
      setFormData({
        city: '',
        area: '',
        barcode: '',
        status: 'inwarehouse',
      });
    }
    setDialogOpen(true);
    setError('');
  };

  const handleSave = async () => {
    if (!formData.city.trim() || !formData.area.trim() || !formData.barcode.trim()) {
      setError('City, area, and barcode are required');
      return;
    }

    try {
      if (editMode) {
        await installationDeviceService.updateDevice(selectedDevice._id, formData);
        setSuccess('Device updated successfully');
      } else {
        await installationDeviceService.createDevice(formData);
        setSuccess('Device created successfully');
      }
      setDialogOpen(false);
      loadDevices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = (device) => {
    setDeviceToDelete(device);
    setDeleteDialogOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    try {
      await installationDeviceService.deleteDevice(deviceToDelete._id);
      setSuccess(`Device "${deviceToDelete.barcode}" deleted successfully`);
      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
      loadDevices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      inwarehouse: 'In Warehouse',
      installed: 'Installed',
      canceled: 'Canceled',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      inwarehouse: 'info',
      installed: 'success',
      canceled: 'error',
    };
    return colors[status] || 'default';
  };

  /**
   * Normalize Excel row keys to model fields (case-insensitive).
   * Expected columns: City, Area, Barcode, Status (optional).
   */
  const normalizeRow = (row) => {
    const keyMap = {};
    Object.keys(row).forEach((key) => {
      const lower = key.trim().toLowerCase();
      if (lower === 'city') keyMap[key] = 'city';
      else if (lower === 'area') keyMap[key] = 'area';
      else if (lower === 'barcode') keyMap[key] = 'barcode';
      else if (lower === 'status') keyMap[key] = 'status';
    });
    const out = {};
    Object.keys(keyMap).forEach((k) => {
      out[keyMap[k]] = row[k];
    });
    return out;
  };

  /**
   * Handle Excel file selection and import
   */
  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      setError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setImporting(true);
    setError('');

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const firstSheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet);

        if (!rows.length) {
          setError('Excel file has no data rows. Ensure the first row contains column headers: City, Area, Barcode, Status.');
          setImporting(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }

        const devices = rows.map((row) => normalizeRow(row)).filter((d) => d.city || d.area || d.barcode);

        if (!devices.length) {
          setError('No valid rows found. Required columns: City, Area, Barcode (Status is optional).');
          setImporting(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }

        const response = await installationDeviceService.importDevices(devices);
        setImportResult(response.data);
        setImportDialogOpen(true);
        loadDevices();
        setSuccess(response.message || 'Import completed.');
        setTimeout(() => setSuccess(''), 5000);
      } catch (err) {
        setError(err.message || 'Import failed.');
      } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsArrayBuffer(file);
  };

  /**
   * Export devices to Excel
   */
  const handleExportToExcel = async () => {
    setExporting(true);
    
    try {
      // Prepare data for Excel
      const excelData = devices.map((device) => ({
        'City': device.city || '',
        'Area': device.area || '',
        'Barcode': device.barcode || '',
        'Status': getStatusLabel(device.status),
        'Created At': new Date(device.createdAt).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit' 
        }),
        'Updated At': new Date(device.updatedAt).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit' 
        }),
      }));

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 20 }, // City
        { wch: 20 }, // Area
        { wch: 25 }, // Barcode
        { wch: 15 }, // Status
        { wch: 15 }, // Created At
        { wch: 15 }, // Updated At
      ];

      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Installation Devices');

      // Generate file name with current date
      const fileName = `installation_devices_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download file
      XLSX.writeFile(wb, fileName);

      alert('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export data: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const filteredDevices = devices.filter((device) =>
    device.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.area?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.barcode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      field: 'city',
      headerName: 'City',
      width: 150,
    },
    {
      field: 'area',
      headerName: 'Area',
      width: 150,
    },
    {
      field: 'barcode',
      headerName: 'Barcode',
      width: 200,
      renderCell: (params) => (
        <Typography variant='body2' sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value)}
          color={getStatusColor(params.value)}
          size='small'
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 150,
      valueGetter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      renderCell: (params) => (
        <Box>
          <Button
            variant='outlined'
            size='small'
            onClick={() => handleOpenDialog(params.row)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant='outlined'
            size='small'
            color='error'
            startIcon={<DeleteIcon />}
            onClick={() => handleDelete(params.row)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Typography variant='h4'>Installation Devices (Warehouse)</Typography>
        <Box>
          <input
            type='file'
            ref={fileInputRef}
            accept='.xlsx,.xls'
            onChange={handleImportFile}
            style={{ display: 'none' }}
          />
          <Button
            variant='outlined'
            color='primary'
            startIcon={importing ? <CircularProgress size={20} color='inherit' /> : <UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            sx={{ mr: 2 }}
          >
            {importing ? 'Importing...' : 'Import from Excel'}
          </Button>
          <Button
            variant='contained'
            color='success'
            startIcon={exporting ? <CircularProgress size={20} color='inherit' /> : <DownloadIcon />}
            onClick={handleExportToExcel}
            disabled={exporting || devices.length === 0}
            sx={{ mr: 2 }}
          >
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Device
          </Button>
        </Box>
      </Box>

      {/* Required columns label / example for Excel import */}
      <Alert severity='info' sx={{ mb: 2 }} icon={false}>
        <Typography variant='subtitle2' fontWeight='bold' gutterBottom>
          Required columns in your Excel file for import:
        </Typography>
        <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
          <li><strong>City</strong> (required) – e.g. Tripoli, Benghazi</li>
          <li><strong>Area</strong> (required) – e.g. District or area name</li>
          <li><strong>Barcode</strong> (required) – unique device barcode (duplicates will be skipped)</li>
          <li><strong>Status</strong> (optional) – one of: <em>In Warehouse</em>, <em>Installed</em>, <em>Canceled</em>. Default: In Warehouse</li>
        </Box>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Example: First row as headers — <code>City</code>, <code>Area</code>, <code>Barcode</code>, <code>Status</code> — then one device per row.
        </Typography>
      </Alert>

      {error && !dialogOpen && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TextField
            label='Search'
            placeholder='Search by city, area, or barcode...'
            variant='outlined'
            fullWidth
            margin='normal'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
          />
          <DataGrid
            rows={filteredDevices}
            columns={columns}
            getRowId={(row) => row._id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            autoHeight
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>

      {/* Create/Edit Device Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>{editMode ? 'Edit Device' : 'Add New Device'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            autoFocus
            margin='dense'
            label='City'
            type='text'
            fullWidth
            variant='outlined'
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
            required
          />

          <TextField
            margin='dense'
            label='Area'
            type='text'
            fullWidth
            variant='outlined'
            value={formData.area}
            onChange={(e) => setFormData({ ...formData, area: e.target.value })}
            sx={{ mb: 2 }}
            required
          />

          <TextField
            margin='dense'
            label='Barcode'
            type='text'
            fullWidth
            variant='outlined'
            value={formData.barcode}
            onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
            sx={{ mb: 2 }}
            required
            disabled={editMode}
            helperText={editMode ? 'Barcode cannot be changed after creation' : ''}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              label='Status'
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <MenuItem value='inwarehouse'>In Warehouse</MenuItem>
              <MenuItem value='installed'>Installed</MenuItem>
              <MenuItem value='canceled'>Canceled</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant='contained'>
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Device Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Device</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography>
            Are you sure you want to delete the device with barcode <strong>"{deviceToDelete?.barcode}"</strong>?
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setDeviceToDelete(null);
            setError('');
          }}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant='contained' color='error'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import result dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Import result</DialogTitle>
        <DialogContent>
          {importResult && (
            <>
              <Typography gutterBottom>
                <strong>Created:</strong> {importResult.created} device(s)
              </Typography>
              {importResult.skipped > 0 && (
                <Typography gutterBottom color='text.secondary'>
                  <strong>Skipped:</strong> {importResult.skipped} (e.g. duplicate barcode)
                </Typography>
              )}
              {importResult.errorsCount > 0 && (
                <Typography color='error' gutterBottom>
                  <strong>Errors:</strong> {importResult.errorsCount} row(s)
                </Typography>
              )}
              {importResult.errorDetails?.length > 0 && (
                <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant='caption' color='text.secondary'>Error details:</Typography>
                  {importResult.errorDetails.slice(0, 10).map((e, i) => (
                    <Typography key={i} variant='body2' color='error'>
                      Row {e.row}: {e.message}
                    </Typography>
                  ))}
                  {importResult.errorDetails.length > 10 && (
                    <Typography variant='body2' color='text.secondary'>
                      ... and {importResult.errorDetails.length - 10} more
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

